import React from 'react';
import { makeStyles, Box, Typography, Button } from '@material-ui/core';
import Link from 'next/link';
import Image from 'next/image';
import classNames from 'classnames';

import CustomA from 'components/ui/links/CustomA';
import { ColorPreview, ProductPreview } from 'domain/types';
import eyeIcon from 'public/icons/eye.svg';
import { addToCart } from 'utils/apollo/vars/cartVar';
import { openCart } from 'utils/apollo/vars/showCartVar';
import events from 'utils/helpers/analytics/events';
import Badge from './Badge/Badge';
import PriceText from 'components/ui/textFormaters/PriceText';
import { calculatePrice } from 'utils/helpers/products/calculatePrice';

interface ProductCardProps {
  product: ProductPreview;
  colors?: Array<ColorPreview>;
  index?: number;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product: {
    sys: { id },
    slug,
    mainImage: { url },
    price,
    discount,
    name,
    category: { slug: categorySlug, name: categoryName },
    available,
    color,
    featuredImage,
    productItemCode
  },
  index = 0
}): JSX.Element => {
  const classes = useStyles();

  const handleBuy = (e: React.SyntheticEvent) => {
    e.preventDefault();
    addToCart(id);
    openCart();

    events.addToCart(
      categoryName,
      name,
      color,
      1,
      calculatePrice(price, discount),
      productItemCode
    );
  };

  return (
    <Link href={`/shop/${categorySlug}/${slug}`} passHref>
      <CustomA>
        <div className={classes.card}>
          {!!available && <Badge featured={!!featuredImage} discount={discount} />}

          <Box className={classes.cardHover} zIndex={1000 - index}>
            <Box className={classes.hoverContent}>
              <div className={classes.eyeButton}>
                <Image
                  src={eyeIcon}
                  alt="Eye"
                  height={52}
                  width={52}
                  className={classes.animatedEye}
                  title="Переглянути"
                />
              </div>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleBuy}
                className={classNames(
                  classes.buyButton,
                  !available && classes.buyButtonNotAvailable
                )}
                disabled={available <= 0}
              >
                {available > 0 ? 'У кошик' : 'Не в наявності'}
              </Button>
            </Box>
          </Box>

          <Box className={classes.imageContainer}>
            <Image
              src={url}
              quality={100}
              width={384}
              height={384}
              className={classNames(classes.image, !available && classes.notAvailable)}
            />
          </Box>

          <Box className={classes.content}>
            <Typography
              variant="h3"
              className={classNames(classes.title, !available && classes.notAvailable)}
            >
              {name}
            </Typography>

            <Typography
              variant="h5"
              component="p"
              color="primary"
              className={classNames(classes.price, !available && classes.notAvailable)}
            >
              <PriceText price={price} discount={discount} isAvailable={!!available} />
            </Typography>
          </Box>
        </div>
      </CustomA>
    </Link>
  );
};

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'relative',
    transition: 'box-shadow 500ms',
    marginBottom: 86,
    height: 'calc(100% - 86px)',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      marginBottom: 0,
      height: '100%',
      '&:hover': {
        boxShadow: '0px 0px 26.68px 2.32px rgba(0, 0, 0, 0.13)',
        '& $cardHover': {
          opacity: 1
        },
        '& $image': {
          transform:
            'perspective(1000px) rotateX(35deg) rotateY(4deg) rotateZ(-30deg) scale(1.25,1.25)'
        }
      }
    }
  },
  cardClicked: {
    [theme.breakpoints.up('md')]: {
      boxShadow: '0px 0px 26.68px 2.32px rgba(0, 0, 0, 0.13)',
      '& $cardHover': {
        opacity: 1
      },
      '& $image': {
        transform:
          'perspective(1000px) rotateX(35deg) rotateY(4deg) rotateZ(-30deg) scale(1.25,1.25)',
        cursor: 'pointer'
      }
    }
  },
  cardHover: {
    position: 'absolute',
    top: 'calc(100% - 16px)',
    left: 0,
    height: 86,
    width: '100%',
    opacity: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    [theme.breakpoints.up('md')]: {
      top: 'calc(100% - 6px)',
      opacity: 0,
      transition: 'all 500ms',
      boxShadow: '0px 30px 26.68px 2.32px rgba(0, 0, 0, 0.13)'
    }
  },
  hoverContent: {
    backgroundColor: theme.palette.common.white,
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(3)
  },
  eyeButton: {
    flexGrow: 1,
    display: 'none',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      display: 'flex'
    }
  },
  buyButton: {
    flexGrow: 2,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: '1rem',
    height: 41,
    paddingBottom: theme.spacing(0.5),
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem',
      height: 47,
      paddingBottom: theme.spacing(1)
    }
  },
  buyButtonNotAvailable: {
    fontSize: '0.75rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '1rem'
    }
  },
  title: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem'
    }
  },
  price: {
    textAlign: 'center',
    fontSize: '0.875rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem'
    }
  },
  content: {
    width: '100%',
    padding: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  imageContainer: {
    overflow: 'hidden'
  },
  image: {
    transition: 'transform 500ms ease-in',
    transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) rotateZ(0deg)'
  },
  notAvailable: {
    opacity: 0.59
  },
  animatedEye: {
    animation: `$blink 6000ms ${theme.transitions.easing.easeInOut} infinite`
  },
  '@keyframes blink': {
    '0%, 100%': {
      transform: 'scale(1, 0.05)'
    },
    '5%, 95%': {
      transform: 'scale(1, 1)'
    }
  }
}));

export default ProductCard;
